import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '../client/cn';
import { ReactNode, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

type MenuButtonGroupProps = {
  title: string;
  isOpen: boolean;
  icon?: ReactNode;
  defaultPath: string;
  onToggle: (groupId: string) => void;
  groupId: string;
  children: ReactNode;
};

const MenuButtonGroup = ({ 
  children, 
  isOpen, 
  title, 
  icon,
  defaultPath,
  onToggle,
  groupId
}: MenuButtonGroupProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!isOpen) {
      navigate(defaultPath);
    }
    onToggle(groupId);
  };

  return (
    <div className="py-1">
      <button
        onClick={handleClick}
        className={cn(
          "w-full group flex items-center justify-between px-3 py-2 text-sm transition-colors",
          "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/50",
          isOpen && "text-gray-900 dark:text-gray-100"
        )}
      >
        <div className="flex items-center gap-2.5">
          {icon && (
            <span className="flex-shrink-0 text-gray-500 dark:text-gray-400">
              {icon}
            </span>
          )}
          <span className="font-medium">{title}</span>
        </div>
        <ChevronDown 
          className={cn(
            "w-4 h-4 text-gray-400 transition-transform duration-300", 
            isOpen && "transform rotate-180"
          )} 
        />
      </button>
      
      <div 
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        )}
        style={{
          transitionProperty: 'max-height, opacity',
          willChange: 'max-height, opacity'
        }}
      >
        <div className="relative ml-3">
          <div className="absolute left-3 top-1 bottom-1 w-px bg-gray-200 dark:bg-gray-700" />
          <div className="pl-5 py-1 space-y-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuButtonGroup;